/*
PURPOSE: Pure collective-impact arithmetic shared by the legacy
CollectiveImpactCalculator and the editorial calculator.

USE CASES:
- CollectiveImpactCalculator (legacy, dark theme, used by PublicCoral)
- EditorialImpactCalculator (editorial theme, used by Home + PublicArtist)

DOMAIN BOUNDARIES:
- Pure functions + constants only. No React, no copy, no analytics.
- The artist share comes from the shared contribution-fee producer. It is NOT
  recomputed here: what reaches an artist is one number with one home.

ASSUMPTIONS:
- A per-artist figure must be derived from what actually REACHES artists, never
  from the gross contribution. Dividing the gross overstates it by 13-22% across
  the slider range, which is a public money claim on the home page.
*/

import { computeContributionFeeBreakdown } from 'shared/utils/coralContributionFee';

export const IMPACT_ARTIST_COUNT = 8;
export const IMPACT_PARTICIPANT_COUNT = 5000;

/** Total CONTRIBUTED at this level, gross. What people pay, not what artists receive. */
export const computeTotalImpact = (contribution: number): number =>
  contribution * IMPACT_PARTICIPANT_COUNT;

/**
 * What reaches artists from one contribution, in dollars. The fee split is produced once,
 * by the shared producer; this only converts at the boundary.
 */
export const artistShareOf = (contribution: number): number => {
  if (!Number.isFinite(contribution) || contribution <= 0) return 0;
  const cents = Math.round(contribution * 100);
  return computeContributionFeeBreakdown(cents).artistShareCents / 100;
};

/** Total REACHING artists at this level, gross less the platform fee. */
export const computeTotalArtistImpact = (contribution: number): number =>
  artistShareOf(contribution) * IMPACT_PARTICIPANT_COUNT;

/**
 * What each artist receives each month. Derived from the artist share, not the gross:
 * the gross includes the platform fee, which never reaches an artist.
 */
export const computePerArtistImpact = (contribution: number): number =>
  computeTotalArtistImpact(contribution) / IMPACT_ARTIST_COUNT;
